/**
 * `@dxf-ai-editor/cad-core` — the block-recursion PLACEHOLDER: the red square
 * + `RECURSION:<block>` label the flatten draws INSTEAD of descending into a
 * cyclic (or depth-capped) nested INSERT, and the block-local footprint the
 * bounds walk must reserve for it.
 *
 * # Why the formula lives here (Codex PR #784 round 7 P2 "Bound the recursion
 *   placeholder instead of dropping it")
 *
 * The renderer deliberately EMITS geometry at a cyclic nested reference
 * (`apps/web/.../typed/blockFromEntity.ts::buildTypedRecursionPlaceholder`),
 * so the INSERT's stored AABB must contain it. Before this module the bounds
 * walk turned that child into `ALWAYS_INCLUDE` and then SKIPPED it: with any
 * resolvable sibling present the stored box covered only the siblings, and a
 * placeholder drawn elsewhere fell outside the RBush record — invisible to
 * cache-direct viewport culling and unpickable.
 *
 * cad-core cannot import from `apps/web` (`.dependency-cruiser.cjs`
 * `cad-core-no-back-edges`), so the placeholder's geometry is defined HERE —
 * square size, label height, label text, corner order — and the renderer
 * CONSUMES it. One truth, so the drawn marker and the reserved box cannot
 * drift (the S10 golden-parity harness deep-equals the legacy placeholder
 * output, so every number below is the legacy `blockInsert.ts:546-611` one).
 *
 * # The scale problem, and how the frame solves it
 *
 * The square's side uses the ACCUMULATED scale at the recursion level
 * (`max(|sx|, |sy|, 1)` — note the world-unit CLAMP, which dominates whenever
 * an ancestor scales down), while the walk builds bounds PER DEFINITION and
 * multiplies by each ancestor INSERT on the way out. A naive local box would
 * therefore under-cover. {@link BlockRecursionFrame} threads the accumulated
 * reference transform down the walk, so the local box is exactly the world
 * footprint mapped back through it ({@link recursionPlaceholderLocalBounds}),
 * and the outward chain reproduces the world square (each outward step
 * re-AABBs, which only GROWS the box — never shrinks it below the square).
 *
 * A definition whose subtree emits a placeholder is consequently
 * SCALE-DEPENDENT and must not be served from the per-name memo computed
 * under a different frame — `blockDefinitionWalk.ts` drops such an entry
 * instead of memoizing it.
 *
 * # Scope note — SELECT geometry
 *
 * This slice covers the BOUNDS walk (the finding's subject: the stored AABB
 * that drives cache-direct culling and the RBush hit test). The fine-grained
 * SELECT geometry (`blockSelectGeometry.ts`) still resolves a cyclic reference
 * to nothing — its PRE-EXISTING behaviour, unchanged here — so a
 * crossing/window test against the placeholder's individual segments falls
 * back to the (now correct) box. Extending the select walk with the same frame
 * is a separate slice; it needs the identical threading and would otherwise
 * widen this fix's blast radius.
 */

import {
  textEntitySchema,
  type BlockInsertEntity,
  type TextEntity,
} from "@dxf-ai-editor/drawing-model";

import {
  composeBlockLinearExact,
  composeNestedBlockLinear,
  guardBlockScale,
  invertBlockLinear,
  applyMat2,
  IDENTITY_BLOCK_LINEAR,
  type BlockLinearRsr,
} from "./blockLinearTransform";
import type { Aabb } from "./common";
import { boundsFromPoints, unionBounds } from "./common";
import { textBounds } from "./leafBounds";

/**
 * Hard recursion-depth cap of the block flatten — the legacy
 * `MAX_BLOCK_RECURSION_DEPTH` (`blockInsert.ts:103`), re-homed here so the
 * renderer's cap and the bounds walk's placeholder predicate are the SAME
 * number (the renderer re-exports it as
 * `MAX_TYPED_BLOCK_RECURSION_DEPTH`).
 */
export const MAX_BLOCK_RECURSION_DEPTH = 50;

/**
 * Side of the placeholder square: the accumulated per-axis scale magnitude,
 * clamped to at least ONE WORLD UNIT so a deeply scaled-down cycle still
 * shows a visible warning marker (legacy parity).
 */
export function recursionPlaceholderSquareSize(
  scaleX: number,
  scaleY: number,
): number {
  return Math.max(Math.abs(scaleX), Math.abs(scaleY), 1);
}

/** Glyph height of the `RECURSION:<block>` label (legacy parity). */
export function recursionPlaceholderLabelHeight(size: number): number {
  return Math.max(size * 0.2, 0.5);
}

/** Contents of the label (legacy parity). */
export function recursionPlaceholderLabelText(blockName: string): string {
  return `RECURSION:${blockName}`;
}

/**
 * The square's four corners, counter-clockwise from the lower-left, centred
 * on the (already transformed) insertion point — the order the renderer
 * closes its four LINE segments in.
 */
export function recursionPlaceholderSquareCorners(
  center: { x: number; y: number },
  size: number,
): Array<{ x: number; y: number }> {
  const half = size * 0.5;
  return [
    { x: center.x - half, y: center.y - half },
    { x: center.x + half, y: center.y - half },
    { x: center.x + half, y: center.y + half },
    { x: center.x - half, y: center.y + half },
  ];
}

/**
 * The label as a TEXT entity — ACI red on layer "0", `styleName: "Standard"`,
 * schema-parsed so every unspecified field takes the SAME default the
 * renderer's own `textEntitySchema.parse` gives it. Both the renderer (which
 * feeds it to the TEXT adapter) and {@link recursionPlaceholderWorldFootprint}
 * (which boxes it with the shared glyph-box math) build it through here.
 */
export function recursionPlaceholderLabelEntity(
  blockName: string,
  size: number,
  at: { x: number; y: number },
  identity: { id: string; spaceId: string },
): TextEntity {
  return textEntitySchema.parse({
    type: "Text",
    id: identity.id,
    spaceId: identity.spaceId,
    layer: "0",
    color: "1", // ACI red — visible warning (legacy parity).
    insertionPoint: { x: at.x, y: at.y },
    height: recursionPlaceholderLabelHeight(size),
    contents: recursionPlaceholderLabelText(blockName),
    styleName: "Standard",
  });
}

/**
 * The placeholder's WORLD footprint as OFFSETS around the (transformed)
 * insertion point: the square UNION the label's rendered glyph box.
 *
 * The label box is `leafBounds.ts::textBounds` — the same one-truth
 * justification-aware glyph box every other TEXT child is bounded by
 * (drawing-model's `textLocalCorners`), never a second text-metrics path.
 */
export function recursionPlaceholderWorldFootprint(
  blockName: string,
  scaleX: number,
  scaleY: number,
): Aabb | null {
  const size = recursionPlaceholderSquareSize(scaleX, scaleY);
  // An accumulated scale that OVERFLOWED to Infinity (schema-valid extreme
  // components multiply out) has no finite footprint — `boundsFromPoints`
  // filters the non-finite corners and yields `null`, which the caller turns
  // back into the conservative pre-existing behaviour.
  const square = boundsFromPoints(
    recursionPlaceholderSquareCorners({ x: 0, y: 0 }, size),
  );
  if (!square) return null;
  const label = textBounds(
    recursionPlaceholderLabelEntity(
      blockName,
      size,
      { x: 0, y: 0 },
      { id: "recursion-label", spaceId: "*Model_Space" },
    ),
  );
  return label ? unionBounds(square, label) : square;
}

/**
 * The accumulated block-reference state at ONE level of the bounds walk —
 * everything the placeholder footprint needs and nothing else.
 *
 * `linear` mirrors the RENDERER's own accumulation (see
 * {@link descendNestedInsertFrame} / {@link descendContentReferenceFrame}),
 * because the placeholder must be bounded where the renderer DRAWS it. Since
 * Codex PR #784 round 8 the two are the SAME map: `composeNestedBlockLinear`
 * composes the exact sequential product for every nested reference (keeping
 * the byte-identical fold only where it provably equals that product — a
 * zero inner rotation, or a uniform ancestor scale), so the earlier S10
 * caveat — a non-uniformly scaled ancestor around a ROTATED nested INSERT,
 * where the plain-triple accumulation folded a shear away — no longer
 * applies. That fix is shared by all block-child geometry, not only
 * placeholders.
 */
export interface BlockRecursionFrame {
  /** INSERT levels descended so far: 0 = world (no reference yet). The
   *  renderer's own recursion `depth` for this frame's definition is
   *  `levels - 1`. */
  readonly levels: number;
  /** The renderer's accumulated linear part for this frame's definition. */
  readonly linear: BlockLinearRsr;
}

/** The world frame — before any block reference. */
export const WORLD_RECURSION_FRAME: BlockRecursionFrame = {
  levels: 0,
  linear: IDENTITY_BLOCK_LINEAR,
};

/** The authored triple of one INSERT, guarded exactly as the flatten guards it. */
function insertTriple(insert: BlockInsertEntity): {
  rotationDeg: number;
  scaleX: number;
  scaleY: number;
} {
  return {
    rotationDeg: Number.isFinite(insert.rotation) ? insert.rotation : 0,
    scaleX: guardBlockScale(insert.scaleX),
    scaleY: guardBlockScale(insert.scaleY),
  };
}

/**
 * Descend one NESTED INSERT — the flatten's OWN accumulation rule
 * (`blockFromEntity.ts`'s nested `Block` arm):
 *
 *   - with NO inner rotation in force, the pre-existing S10 triple fold
 *     (rotations summed, scales multiplied per axis) — byte-identical,
 *     golden-parity-pinned, and deliberately left approximate for a
 *     non-uniformly scaled ancestor;
 *   - with one in force (MLEADER block content — see
 *     {@link descendContentReferenceFrame}), the EXACT composition, because
 *     the fold would silently drop a shear.
 */
export function descendNestedInsertFrame(
  frame: BlockRecursionFrame,
  insert: BlockInsertEntity,
): BlockRecursionFrame {
  return {
    levels: frame.levels + 1,
    linear: composeNestedBlockLinear(frame.linear, insertTriple(insert)),
  };
}

/**
 * Descend a MULTILEADER's BLOCK-content reference — ALWAYS the exact
 * composition (`blockChildMultileader.ts::composeBlockContentReference`): the
 * content's own rotation is an INNER rotation of the enclosing affine, so
 * folding it outward would shear a non-uniformly scaled enclosing INSERT's
 * content.
 */
export function descendContentReferenceFrame(
  frame: BlockRecursionFrame,
  insert: BlockInsertEntity,
): BlockRecursionFrame {
  return {
    levels: frame.levels + 1,
    linear: composeBlockLinearExact(frame.linear, insertTriple(insert)),
  };
}

/**
 * `true` when the flatten would draw a placeholder INSTEAD of descending into
 * `blockName` at `frame` — the renderer's own guard
 * (`visited.has(blockName) || depth >= MAX_BLOCK_RECURSION_DEPTH`), with
 * `inProgress` as the walk's twin of `visited` and `levels - 1` as `depth`.
 */
export function emitsRecursionPlaceholder(
  blockName: string,
  frame: BlockRecursionFrame,
  inProgress: ReadonlySet<string>,
): boolean {
  return (
    inProgress.has(blockName) || frame.levels - 1 >= MAX_BLOCK_RECURSION_DEPTH
  );
}

/**
 * The placeholder's footprint in the ENCLOSING definition's local frame:
 * the world footprint (square ∪ label, sized by `frame`'s accumulated scale)
 * mapped back through the accumulated linear part and re-anchored on the
 * nested reference's own insertion point.
 *
 * Mapping the world box BACK is what makes the reserved box exact rather than
 * merely large: the outward placement chain re-applies the very same linear
 * part (plus an AABB per level, which only grows the result), so the final
 * world box is guaranteed to CONTAIN the square and the label.
 *
 * `null` when the accumulated map is singular / non-finite (a degenerate zero
 * scale) or the world footprint itself overflowed — the caller then keeps the
 * pre-existing conservative `ALWAYS_INCLUDE` behaviour rather than emitting
 * NaN corners.
 */
export function recursionPlaceholderLocalBounds(
  blockName: string,
  insertionPoint: { x: number; y: number },
  enclosing: BlockRecursionFrame,
  reference: BlockRecursionFrame,
): Aabb | null {
  // The SQUARE is sized by the scale accumulated AT the cyclic reference
  // (the renderer's `t.scale` for the level it refuses to descend into)…
  const world = recursionPlaceholderWorldFootprint(
    blockName,
    reference.linear.scaleX,
    reference.linear.scaleY,
  );
  if (!world) return null;
  // …and the world offsets are expressed in the ENCLOSING definition's frame,
  // which is what the outward placement chain will map back out.
  const inv = invertBlockLinear(enclosing.linear);
  if (!inv) return null;
  // Equivalent-mutant note (targeted mutation, PR #784 round 7): seeding this
  // array with a non-point element SURVIVES and is observationally equivalent —
  // `boundsFromPoints` filters every non-finite coordinate out, so the extra
  // entry contributes nothing. Same equivalence `leafBounds.ts::textBounds`
  // already records for its own empty-array branch.
  const corners: Array<{ x: number; y: number }> = [];
  for (const [dx, dy] of [
    [world.minX, world.minY],
    [world.maxX, world.minY],
    [world.maxX, world.maxY],
    [world.minX, world.maxY],
  ] as const) {
    const p = applyMat2(inv, dx, dy);
    corners.push({ x: insertionPoint.x + p.x, y: insertionPoint.y + p.y });
  }
  return boundsFromPoints(corners);
}
